/**
 * PlayChess V2 design tokens — the "Premium Themes" study (Light "Gallery" as
 * the base, Dark as a user theme), transcribed value-for-value from the design
 * artboards L1/D1 (`--bg` … `--piece-b`).
 *
 * Why a local token set and not CCColors: CCColors is a single static LIGHT
 * palette with no dark variant and no elevation/shadow scale, and the V2 play
 * surface is the only place in the app that has two themes. Everything outside
 * `PlayChessV2/**` keeps using CCColors.
 *
 * BRAND NOTE: the CircleChess gold (#FEE7A3 family) and aqua are deliberately
 * NOT used on the play surface in the two Gallery themes. `pri` is the primary
 * CTA colour (ink on light, warm sand on dark), `clock` is the active-clock
 * bronze and `ev` the event/positive sage. The BRAND palette is the third theme
 * (`CIRCLECHESS`), whose accents are CCColors values. The header/drawer around
 * the screen are shared app chrome and stay on CCColors either way.
 *
 * Fonts are the four faces index.web.js registers. There is NO DMSans-Bold
 * file, so 600/700 weights are expressed with `DMSans-SemiBold` as a
 * `fontFamily` (react-native-web maps `fontWeight` onto the family name only
 * when the family has real weights registered, which these do not).
 */

export type V2Tokens = {
  /** page ground behind the Stage and the Rail */
  bg: string;
  /** card / rail / header surface */
  surface: string;
  /** the one step above `surface` (slider knobs, popovers) */
  raised: string;
  /** recessed tile inside a card (toolbar tiles, tc pills, quiet clock) */
  well: string;
  /** card border; transparent in light, a hairline in dark */
  cardline: string;
  /** hairline separator */
  line: string;
  /** stronger hairline: outlined buttons, chips, selected tiles */
  line2: string;
  /** primary text */
  ink: string;
  /** secondary text */
  ink2: string;
  /** muted text (ratings, meta) */
  muted: string;
  /** faintest text (move numbers, hints) */
  faint: string;
  /** primary action background */
  pri: string;
  /** text on `pri` */
  onPri: string;
  /** active clock background */
  clock: string;
  /** text on the active clock */
  onClock: string;
  /** the glow under the active clock and the primary button */
  clockGlow: string;
  /** event colour: draw offer, live dot, my avatar */
  ev: string;
  /** event card background */
  evTint: string;
  /** event card hairline */
  evLine: string;
  /** text on `ev` */
  onEv: string;
  /** the opponent's accent (avatar) */
  alt: string;
  /** text on `alt` */
  onAlt: string;
  /** resign, low time, negative rating delta */
  danger: string;
  /** text on `danger` (the low-time clock's digits) */
  onDanger: string;
  /** the danger wash under a tile that is mid-hold (04h's #FBE6E8, derived) */
  dangerTint: string;
  /**
   * A SELECTED card-tile's fill — the classic lobby's game-option and
   * time-control tiles (old `CCColors.Buttons.TimeControlBackground` #FFF0C8,
   * GameOptionTiles L41-43 / TimeControl L44-46). Distinct from `well`: `well`
   * is a recessed slot, this is an active choice.
   */
  tileSelected: string;
  /**
   * The classic lobby's tab hairline (old `#2623221A`, GameSearch/styles L81).
   * One step stronger than `line`, one weaker than `line2`, and used as a real
   * BORDER colour rather than an inset shadow.
   */
  hairline: string;
  /**
   * A form validation error — the FEN field's "⚠️ Invalid FEN format"
   * (CustomConfig L103, `#ff4444`). Hotter than `danger`, which is the game's
   * destructive colour (resign, low time).
   */
  error: string;
  /**
   * A textual link / toggle: "Show additional settings" and "Parental Controls"
   * (old `CCColors.Green` #3DAB9E, GameSearch/styles L180).
   */
  link: string;
  /**
   * The classic lobby's card-tile shadow (old `#4D3F37` 0/4, opacity 0.05,
   * radius 8 — GameOptionTiles/styles L20-25 and TimeControl/styles L18-23), as
   * the web boxShadow string RNW understands. Softer and flatter than `sh1`,
   * which is the page-card elevation.
   */
  tileShadow: string;
  /**
   * Whether a low clock is drawn as a FILLED danger block with `onDanger`
   * digits (the CircleChess artboard's low-time clock) or keeps the Gallery
   * behaviour, where only the ACTIVE face swaps its bronze for danger.
   *
   * ALWAYS TRUE as of decision D10: under 10 s BOTH clocks turn red, on every
   * theme — the old WEB screen's rule, picked over the old app's active-only
   * one because the opponent's flag is the thing a player is watching for. The
   * token is kept (rather than deleted along with the branch in
   * `stage/Clock/View.tsx`) so the Gallery palettes can take the quieter rule
   * back without touching two Views.
   */
  lowTimeFilled: boolean;
  /** positive rating delta, online dot */
  pos: string;
  /**
   * The dim over the board column behind the result card. It carries its own
   * alpha so the reveal animates plain 0 -> 1 opacity. It is a DARK wash in BOTH
   * palettes: `ink` at 0.35 (M1a) reads as a light veil over a dark board.
   */
  overlayScrim: string;
  /** card shadow (web boxShadow string) */
  sh1: string;
  /** board / modal shadow (web boxShadow string) */
  sh2: string;
  /** top highlight edge (web boxShadow string) */
  hiEdge: string;
  /** the board frame's fill */
  frame: string;
  /** the board frame's inner edge (web boxShadow string) */
  frameEdge: string;
  /** CSS filter for the wordmark on this ground */
  logoFilter: string;
  /** the Chessboard `colorTheme` that belongs to this palette */
  boardTheme: 'gallery' | 'gallery-dark' | 'default';
};

/** L1 Palette — "Light · Gallery". */
export const GALLERY_LIGHT: V2Tokens = {
  bg: '#F3F0E9',
  surface: '#FAF8F3',
  raised: '#FDFBF7',
  well: '#EDE9E0',
  cardline: 'rgba(0,0,0,0)',
  line: 'rgba(31,29,26,0.07)',
  line2: 'rgba(31,29,26,0.13)',
  ink: '#191816',
  ink2: '#46423C',
  muted: '#77716A',
  faint: '#A8A298',
  pri: '#191816',
  onPri: '#FAF8F3',
  clock: '#A07D4F',
  onClock: '#FDFBF7',
  clockGlow: 'rgba(160,125,79,0.45)',
  ev: '#4F6B59',
  evTint: 'rgba(79,107,89,0.09)',
  evLine: 'rgba(79,107,89,0.28)',
  onEv: '#FAF8F3',
  alt: '#566579',
  onAlt: '#F6F4EF',
  danger: '#9A3E37',
  onDanger: '#FAF8F3',
  dangerTint: 'rgba(154,62,55,0.14)',
  tileSelected: 'rgba(160,125,79,0.16)',
  hairline: 'rgba(31,29,26,0.10)',
  error: '#B4453C',
  link: '#4F6B59',
  tileShadow: '0 4px 8px rgba(64,52,36,0.05)',
  lowTimeFilled: true, // D10: both clocks, every theme
  pos: '#4B7659',
  overlayScrim: 'rgba(25,24,22,0.35)',
  sh1: '0 1px 2px rgba(64,52,36,0.04), 0 12px 32px -8px rgba(64,52,36,0.10)',
  sh2: '0 2px 6px rgba(64,52,36,0.05), 0 36px 80px -20px rgba(64,52,36,0.22)',
  hiEdge: 'inset 0 1px 0 rgba(255,255,255,0.8)',
  frame: '#F8F5EF',
  frameEdge: '0 0 0 1px rgba(255,255,255,0.7) inset',
  logoFilter: 'brightness(0) opacity(0.88)',
  boardTheme: 'gallery',
};

/** D1 Palette — the dark variant of the same study. */
export const GALLERY_DARK: V2Tokens = {
  bg: '#0E1012',
  surface: '#15181B',
  raised: '#1B1F23',
  well: '#101316',
  cardline: 'rgba(255,255,255,0.075)',
  line: 'rgba(255,255,255,0.06)',
  line2: 'rgba(255,255,255,0.11)',
  ink: '#E4E6E8',
  ink2: '#B7BCC2',
  muted: '#868C93',
  faint: '#565C63',
  pri: '#CDB590',
  onPri: '#111315',
  clock: '#CDB590',
  onClock: '#111315',
  clockGlow: 'rgba(205,181,144,0.35)',
  ev: '#86A893',
  evTint: 'rgba(134,168,147,0.09)',
  evLine: 'rgba(134,168,147,0.30)',
  onEv: '#0E1012',
  alt: '#7F8EA6',
  onAlt: '#0E1012',
  danger: '#D98378',
  onDanger: '#111315',
  dangerTint: 'rgba(217,131,120,0.16)',
  tileSelected: 'rgba(205,181,144,0.18)',
  hairline: 'rgba(255,255,255,0.10)',
  error: '#E0857A',
  link: '#86A893',
  tileShadow: '0 4px 12px rgba(0,0,0,0.45)',
  lowTimeFilled: true, // D10: both clocks, every theme
  pos: '#8DB89A',
  // a DARK wash: M1a used `ink` at 0.35, which lightens the board in dark mode
  overlayScrim: 'rgba(14,16,18,0.55)',
  sh1: 'inset 0 1px 0 rgba(255,255,255,0.04), 0 16px 40px -12px rgba(0,0,0,0.7)',
  sh2: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 40px 90px -24px rgba(0,0,0,0.9)',
  hiEdge: 'inset 0 1px 0 rgba(255,255,255,0.05)',
  frame: '#181B1F',
  frameEdge:
    '0 0 0 1px rgba(255,255,255,0.08) inset, inset 0 1px 0 rgba(255,255,255,0.07)',
  logoFilter: 'none',
  boardTheme: 'gallery-dark',
};

/**
 * TH_K Palette — "CircleChess", the BRAND theme of the Premium Themes study:
 * "a playroom built from the CircleChess brand: sunny gold for everything you
 * can press, aqua for events, orange for the opponent, on warm beige. Cards sit
 * on a chunky toy-block shadow so they look pressable; the board sits in a thick
 * white tray."
 *
 * Unlike the two Gallery palettes this one is NOT a study invention: every
 * accent is a CCColors value, so the play surface finally agrees with the app
 * chrome around it. Where the artboard's swatch and CCColors differed, CCColors
 * won (`ev` = CCColors.DarkBlueAqua #4b968d for the artboard's "aqua-deep",
 * `danger` = CCColors.Error #CD2C39 for its "--color-red").
 */
export const CIRCLECHESS: V2Tokens = {
  bg: '#FAF6EB', // CCColors.CCTableBg
  surface: '#FFFFFF',
  raised: '#FFFFFF',
  well: '#FBEFC9', // gold tint
  cardline: 'rgba(0,0,0,0)',
  line: 'rgba(77,63,55,0.08)',
  line2: 'rgba(77,63,55,0.16)',
  ink: '#262322', // CCColors.TextColor.Dark
  ink2: '#4D3F37', // CCColors.TextColor.Primary
  muted: '#7A6A5F',
  faint: '#A8A7A6', // CCColors.TextColor.Grey
  pri: '#FACF47', // CCColors.Primary.Default
  onPri: '#4D3F37',
  clock: '#FACF47',
  onClock: '#4D3F37',
  clockGlow: 'rgba(233,185,46,0.55)',
  ev: '#4b968d', // CCColors.DarkBlueAqua
  evTint: '#E8F5F3',
  evLine: 'rgba(61,171,158,0.45)',
  onEv: '#FFFFFF',
  alt: '#E17B21', // CCColors.Orange
  onAlt: '#FFFFFF',
  danger: '#CD2C39', // CCColors.Error
  onDanger: '#FFFFFF',
  dangerTint: 'rgba(205,44,57,0.14)',
  tileSelected: '#FFF0C8', // CCColors.Buttons.TimeControlBackground
  hairline: 'rgba(38,35,34,0.10)', // the old #2623221A
  error: '#ff4444', // CustomConfig L103
  link: '#3DAB9E', // CCColors.Green
  tileShadow: '0 4px 8px rgba(77,63,55,0.05)', // the old tile shadow, verbatim
  lowTimeFilled: true,
  pos: '#3DAB5B',
  overlayScrim: 'rgba(38,35,34,0.35)',
  // the toy-block shadow: a solid offset block, then a soft ambient drop
  sh1: '0 4px 0 #E6DCCB, 0 14px 28px -12px rgba(77,63,55,0.18)',
  sh2: '0 6px 0 #E3D5BC, 0 26px 50px -18px rgba(77,63,55,0.28)',
  // the artboard has no top highlight (`--hi-edge: none`), and 'none' cannot
  // sit inside a compound boxShadow — a zero-width transparent inset is the
  // no-op RNW accepts.
  hiEdge: 'inset 0 0 0 0 transparent',
  frame: '#FFFFFF', // the thick white board tray
  frameEdge: '0 0 0 3px #FBEFC9 inset',
  logoFilter: 'brightness(0) opacity(0.82)',
  // the app's own gold board (CCColors.ChessBoard.Light/Dark), which is exactly
  // what the artboard's --board-light/--board-dark name
  boardTheme: 'default',
};

/**
 * The four registered web font families (index.web.js L13-29).
 * `display` (Thunder) is only for numbers read at a glance: clocks, time
 * controls, the result word, countdowns.
 */
export const FONT = {
  body: 'DMSans-Regular',
  medium: 'DMSans-Medium',
  semibold: 'DMSans-SemiBold',
  display: 'Thunder',
} as const;

export const RADIUS = {
  card: 18,
  panel: 12,
  control: 12,
  badge: 6,
  pill: 999,
  board: 8,
  /** the home CTA and a time-control pill */
  cta: 14,
  /** an event / notice / seek card nested inside a card */
  event: 16,
} as const;

/**
 * The spacing scale. Paddings and gaps that the design markup states as an
 * off-scale number (14, 18, 22 …) stay as literals inside the View that owns
 * them — the rule is "no unexplained number", not "no number".
 */
export const SPACE = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
} as const;

/**
 * Every distinct (family, size, leading) combination the V2 surface uses, named
 * by ROLE. A View spreads one of these into its style object
 * (`{...TYPE.body, color: tokens.ink2}`) — the `no-restricted-syntax` override
 * for `PlayChessV2/**` makes a bare `fontSize`/`fontFamily` a lint error.
 *
 * Entries are transcribed VALUE-FOR-VALUE from the M1a StyleSheets, including
 * the leading each call site omitted: the retrofit must not move a pixel. Where
 * two roles share a family and size they still get their own entry, because the
 * design overhaul will move them apart.
 *
 * `fontVariant: ['tabular-nums']` is deliberately NOT in here: RN types it as a
 * mutable array, which an `as const` object cannot produce. Numeric faces pass
 * `numeric` to `ui/Txt` instead.
 */
export const TYPE = {
  /** uppercase section label: MOVES · RATING · OPPONENT · QUICK PLAY */
  kicker: {fontFamily: FONT.semibold, fontSize: 11, letterSpacing: 1.32},
  /** the tiny uppercase kind under a time-control pill */
  kickerTc: {fontFamily: FONT.semibold, fontSize: 10, letterSpacing: 1.4},
  /** the milestone badge on an inert card */
  badge: {fontFamily: FONT.semibold, fontSize: 10, letterSpacing: 0.6},
  /** the 11 px hint beside a kicker ("← → to browse") */
  hint: {fontFamily: FONT.body, fontSize: 11},
  /** 12/16 secondary copy inside a card */
  caption: {fontFamily: FONT.body, fontSize: 12, lineHeight: 16},
  /** 12/15 secondary copy in a dense row (dock sub, player meta) */
  captionTight: {fontFamily: FONT.body, fontSize: 12, lineHeight: 15},
  /** 13/18 body copy */
  body: {fontFamily: FONT.body, fontSize: 13, lineHeight: 18},
  /** 13 body copy on the element's own leading (notation numbers, CTA meta) */
  bodyFlat: {fontFamily: FONT.body, fontSize: 13},
  /** 14/20 body copy (the result's end-type line) */
  bodyLg: {fontFamily: FONT.body, fontSize: 14, lineHeight: 20},
  /** 14 body copy on its own leading ("Game starts in") */
  bodyLgFlat: {fontFamily: FONT.body, fontSize: 14},
  /** the rail header chip */
  chip: {fontFamily: FONT.medium, fontSize: 12},
  /** a 12 px control label (the strip's action) */
  labelSm: {fontFamily: FONT.semibold, fontSize: 12},
  /** the toolbar tile's label */
  tileLabel: {fontFamily: FONT.semibold, fontSize: 12, lineHeight: 14},
  /** a 13 px control label (outline buttons, the notice action) */
  label: {fontFamily: FONT.semibold, fontSize: 13},
  /** a 14 px control label (the result footer) */
  button: {fontFamily: FONT.semibold, fontSize: 14},
  /** the home CTA's label */
  cta: {fontFamily: FONT.semibold, fontSize: 15},
  /** a card's title */
  cardTitle: {fontFamily: FONT.semibold, fontSize: 15, lineHeight: 20},
  /** the player row's name */
  rowName: {fontFamily: FONT.semibold, fontSize: 15, lineHeight: 18},
  /** a result seat's name */
  seatName: {fontFamily: FONT.semibold, fontSize: 15, lineHeight: 19},
  /** the matched card's name */
  name: {fontFamily: FONT.semibold, fontSize: 17, lineHeight: 22},
  /** the result's message line */
  message: {fontFamily: FONT.semibold, fontSize: 17, lineHeight: 23},
  /** a page title */
  title: {
    fontFamily: FONT.semibold,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.24,
  },
  /** one move in the notation */
  san: {fontFamily: FONT.medium, fontSize: 15, lineHeight: 20},
  /** the ply the board is showing */
  sanCurrent: {fontFamily: FONT.semibold, fontSize: 15, lineHeight: 20},
  /** the ½ on the draw tile — type, not an icon */
  glyph: {fontFamily: FONT.semibold, fontSize: 20, lineHeight: 18},
  /** the × that dismisses the result */
  closeGlyph: {fontFamily: FONT.medium, fontSize: 20, lineHeight: 22},
  /** avatar initials, by disc size */
  initialsSm: {fontFamily: FONT.semibold, fontSize: 11, letterSpacing: 0.22},
  initialsMd: {fontFamily: FONT.semibold, fontSize: 13, letterSpacing: 0.26},
  initialsLg: {fontFamily: FONT.semibold, fontSize: 14, letterSpacing: 0.28},
  /** avatar initials in the dense player row (a 44 px disc, tighter type) */
  initialsRow: {fontFamily: FONT.semibold, fontSize: 12, letterSpacing: 0.24},
  /** a time-control pill's digits */
  digits: {fontFamily: FONT.display, fontSize: 32, lineHeight: 32},
  /** the result's score */
  score: {fontFamily: FONT.display, fontSize: 30, lineHeight: 32},
  /** the clock face */
  clock: {
    fontFamily: FONT.display,
    fontSize: 38,
    lineHeight: 40,
    letterSpacing: 0.76,
  },
  /** the rating delta */
  delta: {fontFamily: FONT.display, fontSize: 40, lineHeight: 42},
  /** a dock countdown */
  countdown: {fontFamily: FONT.display, fontSize: 44, lineHeight: 44},
  /** the matched card's 3 · 2 · 1 */
  countdownLg: {fontFamily: FONT.display, fontSize: 44, lineHeight: 46},
  /** the new rating */
  rating: {fontFamily: FONT.display, fontSize: 56, lineHeight: 58},
  /** the outcome word */
  outcome: {
    fontFamily: FONT.display,
    fontSize: 92,
    lineHeight: 94,
    letterSpacing: 1.84,
  },
  /** the board's 3 · 2 · 1 digit */
  boardDigit: {fontFamily: FONT.display, fontSize: 92, lineHeight: 96},
  // ── the classic lobby (M2): the old panel's faces, by role ───────────────
  /** the helper line over the FEN field (old FontSizes[10], CustomConfig L83) */
  helper: {fontFamily: FONT.body, fontSize: 10, lineHeight: 14},
  /** the FEN field's error line (old FontSizes[8], CustomConfig L104) */
  fieldError: {fontFamily: FONT.body, fontSize: 8, lineHeight: 12},
  /** a colour chip's label (old FontSizes[12], CustomConfig L173) */
  chipLabel: {fontFamily: FONT.body, fontSize: 12, lineHeight: 16},
  /** a game-option tile's label (old FontSizes[13], GameOptionTiles L90) */
  optionLabel: {fontFamily: FONT.body, fontSize: 13, lineHeight: 17},
  /** a tab's label and a time-control tile's digits (old CCText default 16) */
  tabLabel: {fontFamily: FONT.medium, fontSize: 16, lineHeight: 20},
  /** "Show additional settings" / "Parental Controls" (old FontSizes[14/16]) */
  link: {fontFamily: FONT.body, fontSize: 14, lineHeight: 18},
  /** "Time Controls" (old weight 300 / 16, GameSearch/styles L147-152) */
  sectionTitle: {fontFamily: FONT.body, fontSize: 16, lineHeight: 22},
  /** "Learn while you Play" (old FontSizes[18], GameSearch/styles L135-140) */
  sectionTitleLg: {fontFamily: FONT.body, fontSize: 18, lineHeight: 24},
  /** the design lab's own chrome (exempt from the override, still tokenised) */
  labTitle: {fontFamily: FONT.semibold, fontSize: 16},
  labPacket: {fontFamily: FONT.body, fontSize: 11, lineHeight: 15},
} as const;

/**
 * Fixed sizes. The units that used to own these as local constants
 * (`DOCK_HEIGHT`, `STRIP_HEIGHT`, `ROW_HEIGHT`, `TILE_HEIGHT`) now re-export
 * them, so the existing imports keep working.
 */
export const SIZE = {
  /** a 40 px control: the md button, a dock card's action */
  control: 40,
  /** the result footer's buttons */
  buttonLg: 48,
  /** the strip's inline action */
  buttonSm: 24,
  /** the home CTA */
  cta: 56,
  /** a toolbar tile — and a player row, which is the same 60 px slot */
  tile: 60,
  /** the event dock's slot (`DOCK_HEIGHT`) */
  dock: 88,
  /** the global strip (`STRIP_HEIGHT`) */
  strip: 36,
  /** the rail header */
  railHeader: 56,
  /** a rail header chip */
  chip: 28,
  /** a lab / toggle pill */
  chipLg: 30,
  /** a milestone badge */
  badge: 22,
  /** the notation's nav buttons */
  navButton: 32,
  /** the nav row under the notation */
  navRow: 36,
  /** one notation row (`ROW_HEIGHT`) */
  moveRow: 30,
  /** the notation's number column (`NUMBER_WIDTH`) */
  moveNumber: 28,
  /** one SAN cell (`PLY_WIDTH`) */
  movePly: 110,
  /** one SAN chip */
  moveChip: 24,
  /** one captured piece */
  capturedPiece: 16,
  /**
   * The captured stacks' clip width. The worst case (8p 2n 2b 2r 1q) stacks to
   * exactly 120 px, so 120 clipped it — 160 leaves the tray headroom.
   */
  capturedMax: 160,
  /** avatar discs: a dock card / a result seat / a 44 px row or reveal */
  avatarSm: 32,
  avatarMd: 40,
  avatarLg: 44,
  /** a time-control pill */
  pill: 66,
  /** the clock face */
  clock: 44,
  clockMinWidth: 92,
  /** the board's 3 · 2 · 1 disc */
  countdown: 120,
  /** the result card's max width */
  resultCardMax: 468,
  /** the home page's max width */
  homeMax: 1040,
  // ── the classic lobby (M2) ────────────────────────────────────────────────
  /** a game-option tile — the old `vs(150)` (GameOptionTiles/styles L8) */
  optionTile: 150,
  /** a game-option tile's Rive scene (GameOptionTiles/styles L27-31) */
  optionRive: 60,
  /** a time-control tile's Lottie (TimeControl/styles L32-36) */
  tcLottie: 24,
  /** a tab's icon (GameSearch L390 / L449-452 / L549-552) */
  tabIcon: 22,
  /** the FEN field and a colour chip (CustomConfig L93 / L161) */
  field: 48,
  /** the tick disc on a selected colour chip (CustomConfig L177-185) */
  tickDisc: 22,
  /** the searching / challenge Rive card */
  progressCard: 360,
} as const;

/**
 * The idle banner's gradient, per game mode (index.js L4795-4807). It is NOT a
 * palette token: the banner is the OLD component reused by import (M2 §3.2),
 * it takes its two colours as props, and it is the same brand gradient in every
 * theme. It lives here because `theme/` is where literal colours are allowed.
 */
export const BANNER_GRADIENT = {
  play: {start: '#3DAB9E', stop: '#1E8175'},
  learn: {start: '#937ADB', stop: '#7F62D1'},
  tournaments: {start: '#FBA0E3', stop: '#FBA0E3'},
} as const;

/** `src/Utils/motion.ts`'s curve — the app's one easing. */
export const MOTION = {
  ms: 180,
  easing: 'cubic-bezier(0.2,0.8,0.3,1)',
} as const;

// NOTE: the Stage/Rail measurements that the SIZING FORMULA depends on live in
// `theme/useV2Ui.ts` as `LAYOUT`, next to `computeDesktopBoardSize`, so the
// formula and the StyleSheets cannot drift apart. Nothing layout-sized lives
// here.

export type V2ThemeName = 'light' | 'dark' | 'circlechess';

export const TOKENS: Record<V2ThemeName, V2Tokens> = {
  light: GALLERY_LIGHT,
  dark: GALLERY_DARK,
  circlechess: CIRCLECHESS,
};

/** How each theme names itself in the UI (the switch chips, the theme tile). */
export const THEME_LABELS: Record<V2ThemeName, string> = {
  light: 'Light',
  dark: 'Dark',
  circlechess: 'CircleChess',
};
